import React from 'react';
import {View} from 'react-native';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import {AccountScreenAction} from '../../../models/navigationTypes';

import {RemoveInternalThemeProvider} from './decorators/RemoveInternalThemeProvider';
import {createMockNavigation, createMockRoute} from './mocks/navigationMock';
import {WithMockStoreData} from './mocks/storeMock';
import AccountCreateScreenLoyaltyEnabled from './AccountCreateScreenLoyaltyEnabled';

const meta = {
  title: 'Screens/Account/AccountCreateScreen',
  component: AccountCreateScreenLoyaltyEnabled,
  decorators: [
    RemoveInternalThemeProvider, // Remove hardcoded ThemeProvider to enable brand switching
    Story => (
      <View style={{flex: 1, backgroundColor: '#fff'}}>
        <Story />
      </View>
    ),
  ],
  parameters: {
    layout: 'fullscreen',
    docs: {
      description: {
        component:
          'Multi-mode account creation/update screen with React Hook Form + Zod validation, password strength checking, and loyalty rewards opt-in.\n\n' +
          '**Brand Switching**: Use the brand selector in the toolbar to preview different brand themes (Bottlemart, SipnSave, Harry Brown, Thirsty Camel).',
      },
    },
  },
} satisfies Meta<typeof AccountCreateScreenLoyaltyEnabled>;

export default meta;

type Story = StoryObj<typeof meta>;

/**
 * New Account Creation
 *
 * Primary use case showing all CREATE mode fields:
 * - First name, Last name
 * - Email (pre-filled, disabled)
 * - Password with strength validation
 * - Billing address
 * - Date of birth
 * - Mobile number
 * - Loyalty rewards opt-in
 * - Optional signup code
 */
export const NewAccountCreation: Story = {
  name: 'Create New Account',
  render: () => {
    const navigation = createMockNavigation();
    const route = createMockRoute(AccountScreenAction.CREATE, 'test@example.com');

    return (
      <AccountCreateScreenLoyaltyEnabled navigation={navigation as any} route={route as any} />
    );
  },
  parameters: {
    docs: {
      description: {
        story:
          'New user creating an account. Shows all fields including password, billing address, DOB, mobile number, loyalty opt-in, and signup code.\n\n' +
          '**Form Behavior:**\n' +
          '- Password strength is validated via DEV API\n' +
          '- If weak password is entered, a checkbox appears to bypass validation\n' +
          '- Mobile verification code sent via DEV API (real SMS in DEV environment)\n' +
          '- All form validation is active (required fields, email format, etc.)',
      },
    },
  },
};

/**
 * Update Existing Account
 *
 * Logged-in user updating their account details.
 * Pre-populated with existing data, no password required.
 *
 * Demonstrates:
 * - Form pre-filled with user data from mock userStore
 * - Date of birth field is read-only (can't change after initial creation)
 * - Mobile number change triggers verification flow (mocked)
 * - "Save" button instead of "Next" button
 */
export const UpdateExistingAccount: Story = {
  name: 'Update Account Details',
  decorators: [WithMockStoreData], // Pre-fill form with mock user data
  render: () => {
    const navigation = createMockNavigation();
    const route = createMockRoute(AccountScreenAction.UPDATE);

    return (
      <AccountCreateScreenLoyaltyEnabled navigation={navigation as any} route={route as any} />
    );
  },
  parameters: {
    docs: {
      description: {
        story:
          'Logged-in user updating their account. Form is pre-populated with mock user data:\n\n' +
          '- **Name:** John Smith\n' +
          '- **Email:** john.smith@example.com\n' +
          '- **Phone:** 0412345678\n' +
          '- **Address:** 123 Main Street, Sydney NSW 2000\n' +
          '- **DOB:** March 15, 1985 (read-only)\n\n' +
          '**Mobile Verification:** If you change the phone number and save, verification code is sent via DEV API.',
      },
    },
  },
};
